import { useCallback, useLayoutEffect, useRef } from "react";
import { isReducedMotionPreferred } from "@helpers/reducedMotion";
import { type NativeInvoiceWorkspaceStep } from "./nativeInvoiceWorkspaceSteps";

const INVOICE_PAPER_ATTRIBUTE = "data-invoice-paper";
const INVOICE_FLIGHT_TARGET_ATTRIBUTE = "data-invoice-flight-target";

// Container-transform flight: the paper glides in a straight line while
// scaling into the destination rect. Soft start, long soft landing.
//
// Object permanence is the whole trick: the ghost is a clone of the real
// paper, flies fully opaque the entire way, and lands exactly on the pixels
// where the real element reappears — the swap happens only on touchdown,
// under a fade short enough to read as the object settling, not dissolving.
//
// Only compositor properties (transform, opacity) are animated: the step
// switch floods the main thread (unmounting the items grid, mounting the
// send panel), and anything paint-bound — an animated border-radius, an
// overflow clip that changes per frame — visibly stutters against the
// GPU-driven transform. The wrapper's clip radius is static; the transform
// scales it along with the content, like a physical object shrinking.
const FLIGHT_DURATION_MS = 600;
const FLIGHT_EASING = "cubic-bezier(0.45, 0.05, 0.15, 1)";
// Masks the only real differences at touchdown (the slot's inner corner
// rounding vs the paper's scaled-down corners); anything longer reads as a
// mid-air dissolve instead of a landing.
const LANDING_FADE_MS = 120;

// Which element the flying ghost is cloned from. Cloning the small attachment
// thumbnail and blowing it up ~9x for the return trip landed blurry and
// misaligned (its 1px border and padding scale up with it), so the return
// flight instead clones the full-size destination paper and shrinks its
// starting frame down to the thumbnail rect — the landing is then
// pixel-identical to the real element by construction.
type FlightGhostSource = "source" | "target";

interface FlightRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface PendingFlight {
    rect: FlightRect;
    // Clone of the departing element; null when the ghost is built from the
    // destination instead (ghostFrom === "target"), which can only happen
    // once the destination has rendered.
    ghost: HTMLElement | null;
    // The step whose render this flight lands on; a stale capture for another
    // step is dropped instead of animated.
    landingStep: NativeInvoiceWorkspaceStep;
    targetAttribute: string;
    ghostFrom: FlightGhostSource;
    boxShadow: string;
    cornerRadiusPx: number;
}

// Visual-to-layout ratio: the papers render under CSS zoom (and the email
// card under transform: scale), so layout units must be converted through
// this ratio to get what's actually painted on screen.
function getVisualZoom(element: HTMLElement, rect: FlightRect): number {
    return element.offsetWidth > 0 ? rect.width / element.offsetWidth : 1;
}

function getVisualCornerRadiusPx(
    element: HTMLElement,
    rect: FlightRect,
): number {
    const radius = Number.parseFloat(
        window.getComputedStyle(element).borderRadius,
    );
    if (!Number.isFinite(radius)) {
        return 0;
    }
    return radius * getVisualZoom(element, rect);
}

// The attachment thumb is a bordered slot around the miniature preview; the
// flying paper must coincide with the miniature, not with the border ring,
// so the thumb's endpoint is its content box. The paper endpoints are the
// paper itself, so its border box is already the right rect.
function getFlightRect(element: HTMLElement): FlightRect {
    const rect = element.getBoundingClientRect();
    if (!element.hasAttribute(INVOICE_FLIGHT_TARGET_ATTRIBUTE)) {
        return rect;
    }
    const ratio = getVisualZoom(element, rect);
    return {
        left: rect.left + element.clientLeft * ratio,
        top: rect.top + element.clientTop * ratio,
        width: element.clientWidth * ratio,
        height: element.clientHeight * ratio,
    };
}

// The slot's frame stays on screen during the flight — an empty socket the
// paper visibly lands into — so only the miniature inside it is blanked.
// The full-size paper (the return trip's landing) hides entirely.
function setFlightEndpointHidden(target: HTMLElement, isHidden: boolean): void {
    const isSlot = target.hasAttribute(INVOICE_FLIGHT_TARGET_ATTRIBUTE);
    const element =
        isSlot && target.firstElementChild instanceof HTMLElement
            ? target.firstElementChild
            : target;
    element.style.visibility = isHidden ? "hidden" : "";
}

function buildFlightKeyframes({
    ghostFrom,
    sourceRect,
    targetRect,
}: {
    ghostFrom: FlightGhostSource;
    sourceRect: FlightRect;
    targetRect: FlightRect;
}): Keyframe[] {
    const dx = targetRect.left - sourceRect.left;
    const dy = targetRect.top - sourceRect.top;
    // A single width-based factor keeps the zoom uniform (no corner/content
    // distortion); the page inside the slot is width-fitted the same way, so
    // width alignment is content alignment.
    const scale = targetRect.width / sourceRect.width;

    if (ghostFrom === "target") {
        // The wrapper sits at the destination rect; the animation starts
        // shrunk down over the departed source and relaxes to identity.
        const startScale = sourceRect.width / targetRect.width;
        return [
            {
                transform:
                    `translate(${-dx}px, ${-dy}px) ` + `scale(${startScale})`,
            },
            { transform: "translate(0, 0) scale(1)" },
        ];
    }
    return [
        { transform: "translate(0, 0) scale(1)" },
        { transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
    ];
}

// The ghost must paint at exactly the wrapper's size, so its zoom is pinned
// to the measured visual/layout ratio of the element it clones instead of
// trusting the inline style the clone happened to carry. The wrapper owns
// the shadow (the clip would chop the ghost's own copy).
function prepareGhost(
    ghost: HTMLElement,
    original: HTMLElement,
    rect: FlightRect,
): void {
    ghost.style.margin = "0";
    ghost.style.boxShadow = "none";
    // The capture may happen while the element carries an inline
    // visibility: hidden (target-cloned ghosts), which the clone inherits.
    ghost.style.visibility = "visible";
    ghost.style.setProperty("zoom", String(getVisualZoom(original, rect)));
}

function capturePendingFlight({
    sourceAttribute,
    landingStep,
    targetAttribute,
    ghostFrom,
}: {
    sourceAttribute: string;
    landingStep: NativeInvoiceWorkspaceStep;
    targetAttribute: string;
    ghostFrom: FlightGhostSource;
}): PendingFlight | null {
    const source = document.querySelector(`[${sourceAttribute}]`);
    if (!(source instanceof HTMLElement)) {
        return null;
    }
    const rect = getFlightRect(source);
    let ghost: HTMLElement | null = null;
    if (ghostFrom === "source") {
        const clone = source.cloneNode(true);
        if (!(clone instanceof HTMLElement)) {
            return null;
        }
        prepareGhost(clone, source, rect);
        ghost = clone;
    }
    return {
        rect,
        ghost,
        landingStep,
        targetAttribute,
        ghostFrom,
        boxShadow: window.getComputedStyle(source).boxShadow,
        cornerRadiusPx: getVisualCornerRadiusPx(source, rect),
    };
}

// Animates the invoice paper "flying" between the step-1 document preview and
// the attachment thumbnail of the step-2 email preview, in both directions.
// The matching capture function must be called in the same commit that
// switches the step (while the source element is still mounted); the layout
// effect then runs a FLIP-style ghost overlay from the captured rect to the
// target's rect once the destination step has rendered.
export function useInvoiceFlight(step: NativeInvoiceWorkspaceStep): {
    captureInvoiceForFlight: () => void;
    captureEmailForFlight: () => void;
} {
    const pendingRef = useRef<PendingFlight | null>(null);

    const captureInvoiceForFlight = useCallback(() => {
        pendingRef.current = capturePendingFlight({
            sourceAttribute: INVOICE_PAPER_ATTRIBUTE,
            landingStep: "send",
            targetAttribute: INVOICE_FLIGHT_TARGET_ATTRIBUTE,
            ghostFrom: "source",
        });
    }, []);

    const captureEmailForFlight = useCallback(() => {
        pendingRef.current = capturePendingFlight({
            sourceAttribute: INVOICE_FLIGHT_TARGET_ATTRIBUTE,
            landingStep: "details",
            targetAttribute: INVOICE_PAPER_ATTRIBUTE,
            ghostFrom: "target",
        });
    }, []);

    useLayoutEffect(() => {
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (
            !pending ||
            pending.landingStep !== step ||
            pending.rect.width <= 0
        ) {
            return undefined;
        }
        const target = document.querySelector(`[${pending.targetAttribute}]`);
        if (!(target instanceof HTMLElement)) {
            return undefined;
        }
        if (isReducedMotionPreferred()) {
            return undefined;
        }
        const isGhostFromTarget = pending.ghostFrom === "target";
        const wrapper = document.createElement("div");
        wrapper.style.position = "fixed";
        wrapper.style.zIndex = "10000";
        wrapper.style.pointerEvents = "none";
        // Classic top-left FLIP math: translate the corner, scale around it.
        wrapper.style.transformOrigin = "top left";
        wrapper.style.willChange = "transform";
        // Safety clip: a ghost that laid out different from the element it
        // cloned must never spill past the frame that flies; the static
        // radius scales with the transform, no per-frame paint.
        wrapper.style.overflow = "hidden";

        const placeWrapper = (rect: FlightRect) => {
            wrapper.style.top = `${rect.top}px`;
            wrapper.style.left = `${rect.left}px`;
            wrapper.style.width = `${rect.width}px`;
            wrapper.style.height = `${rect.height}px`;
        };

        if (!isGhostFromTarget && pending.ghost) {
            // Source-cloned ghost: mounts immediately over the departing
            // element so the swap of steps never shows a gap.
            placeWrapper(pending.rect);
            wrapper.style.borderRadius = `${pending.cornerRadiusPx}px`;
            if (pending.boxShadow !== "none") {
                wrapper.style.boxShadow = pending.boxShadow;
            }
            wrapper.appendChild(pending.ghost);
            document.body.appendChild(wrapper);
        }
        // Target-cloned ghosts can only be built once the destination has
        // settled its layout, inside the animation frame below.

        // The landing spot stays blank while the ghost is in flight.
        setFlightEndpointHidden(target, true);

        let flight: Animation | null = null;
        let landingFade: Animation | null = null;
        let takeoffFrame: number | undefined;
        const settle = () => {
            setFlightEndpointHidden(target, false);
            wrapper.remove();
        };

        // Touchdown: reveal the real element under the ghost and let the
        // ghost evaporate in place.
        const handleTouchdown = () => {
            setFlightEndpointHidden(target, false);
            landingFade = wrapper.animate([{ opacity: 1 }, { opacity: 0 }], {
                duration: LANDING_FADE_MS,
                easing: "linear",
                fill: "forwards",
            });
            landingFade.onfinish = () => wrapper.remove();
            landingFade.oncancel = () => wrapper.remove();
        };

        // Frame 1: the previews first render their paper at zoom 1 and only
        // settle to the fitted scale in a follow-up (pre-paint) render, so a
        // rect measured in this same commit is hugely inflated. Waiting one
        // frame measures the settled layout; the ghost meanwhile sits static
        // over the source position, reading as the preview holding still.
        const frame = requestAnimationFrame(() => {
            const measuredRect = getFlightRect(target);
            if (measuredRect.width <= 0) {
                settle();
                return;
            }

            if (isGhostFromTarget) {
                const clone = target.cloneNode(true);
                if (!(clone instanceof HTMLElement)) {
                    settle();
                    return;
                }
                prepareGhost(clone, target, measuredRect);
                placeWrapper(measuredRect);
                wrapper.style.borderRadius = `${getVisualCornerRadiusPx(target, measuredRect)}px`;
                const targetShadow = window.getComputedStyle(target).boxShadow;
                if (targetShadow !== "none") {
                    wrapper.style.boxShadow = targetShadow;
                }
                wrapper.appendChild(clone);
                // The ghost must already sit over the departed source this
                // frame, not jump there when the flight starts next frame.
                const [firstFrame] = buildFlightKeyframes({
                    ghostFrom: pending.ghostFrom,
                    sourceRect: pending.rect,
                    targetRect: measuredRect,
                });
                if (typeof firstFrame.transform === "string") {
                    wrapper.style.transform = firstFrame.transform;
                }
                document.body.appendChild(wrapper);
            }

            // Frame 2: take off only after the heavy step-switch paint has
            // hit the screen, so the flight's first frames don't land in the
            // commit's dropped-frame window. Late layout settles (e.g. the
            // pager strip appearing rescales the paper) are picked up by
            // re-measuring at takeoff.
            takeoffFrame = requestAnimationFrame(() => {
                const targetRect = getFlightRect(target);
                if (targetRect.width <= 0) {
                    settle();
                    return;
                }
                if (isGhostFromTarget) {
                    placeWrapper(targetRect);
                }
                flight = wrapper.animate(
                    buildFlightKeyframes({
                        ghostFrom: pending.ghostFrom,
                        sourceRect: pending.rect,
                        targetRect,
                    }),
                    {
                        duration: FLIGHT_DURATION_MS,
                        easing: FLIGHT_EASING,
                        fill: "forwards",
                    },
                );
                flight.onfinish = handleTouchdown;
                flight.oncancel = settle;
            });
        });

        return () => {
            cancelAnimationFrame(frame);
            if (takeoffFrame !== undefined) {
                cancelAnimationFrame(takeoffFrame);
            }
            landingFade?.cancel();
            if (flight) {
                flight.cancel();
            } else {
                settle();
            }
        };
    }, [step]);

    return { captureInvoiceForFlight, captureEmailForFlight };
}
